import type { Reminder } from '../core/reminders'
import { deliveryMode } from './delivery'
import type { Platform } from './platform'
import type { Storage } from './storage'
import { appStorage, useDeviceStorage } from './web'

/**
 * Площадка «приложение из магазина»: страница та же, что на сайте, но вокруг неё Android.
 *
 * Хранилище — не localStorage, а `SharedPreferences` через мост `MoonwakeBridge`: под теми же
 * ключами, что писала прежняя версия приложения, поэтому обновление ничего не теряет, а часы
 * продолжают синхронизироваться. Будильники ставит система, поэтому ни сервер, ни push тут не нужны:
 * уведомление придёт даже при выключенном экране и без интернета.
 */

interface MoonwakeBridge {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
  remove(options: { key: string }): Promise<void>
  keys(): Promise<{ keys: string[] }>
  stateChanged(): Promise<void>
  /** Напоминания с готовым текстом: в оболочке их ставит система будильниками. */
  setReminders(options: { reminders: Reminder[] }): Promise<void>
  notificationsAllowed(): Promise<{ allowed: boolean }>
  rememberSession(options: { token: string }): Promise<void>
  vibrate(options: { double: boolean }): Promise<void>
  /** Системное «Поделиться» файлом: у WebView нет ни загрузок, ни Web Share (выгрузка Excel, 20.10.2026). */
  shareFile(options: { name: string; mime: string; base64: string }): Promise<void>
  addListener(
    event: 'prefsChanged',
    handler: (data: { key: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>
}

/** Есть ли вокруг страницы Android-оболочка. Глобальный `Capacitor` появляется до загрузки страницы. */
export function isNative(): boolean {
  const capacitor = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  return capacitor?.isNativePlatform?.() === true
}

class BridgeStorage implements Storage {
  private listeners = new Set<(key: string) => void>()

  constructor(private readonly bridge: MoonwakeBridge) {
    void bridge.addListener('prefsChanged', ({ key }) => {
      for (const listener of this.listeners) listener(key)
    })
  }

  async get(key: string): Promise<string | null> {
    // JSObject выбрасывает поле со значением null, поэтому в JS приходит undefined:
    // без приведения к null чтение по частям не поймёт, где конец значения.
    return (await this.bridge.get({ key })).value ?? null
  }

  async set(key: string, value: string): Promise<void> {
    await this.bridge.set({ key, value })
  }

  async remove(key: string): Promise<void> {
    await this.bridge.remove({ key })
  }

  async keys(): Promise<string[]> {
    return (await this.bridge.keys()).keys
  }

  onChange(callback: (key: string) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }
}

let saved: MoonwakeBridge | null = null

/**
 * Отдаёт ключ сессии системной части приложения. Без него она не сможет отправить
 * на сервер то, что накопилось, пока приложение было закрыто: окно, начатое на часах,
 * и фазы, пройденные при выключенном экране.
 */
export async function shareSessionWithNative(token: string): Promise<void> {
  if (!isNative() || saved === null) return
  await saved.rememberSession({ token }).catch(() => {})
}

/**
 * Подключить хранилище приложения — настройки Android через мост.
 *
 * Отдельно от создания площадки, потому что звать это надо раньше: список малышей
 * читается до неё, и если основу не подменить, список ляжет в localStorage,
 * а данные — в настройки. Так на Android второй малыш и затирал первого.
 */
/** Отдать файл через системное «Поделиться» оболочки. Вне оболочки — ошибка: зовут только из неё. */
export async function nativeShareFile(name: string, mime: string, bytes: Uint8Array): Promise<void> {
  if (!isNative() || saved === null) throw new Error('нет моста')
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  await saved.shareFile({ name, mime, base64: btoa(bin) })
}

export async function connectNativeStorage(): Promise<void> {
  const { registerPlugin } = await import('@capacitor/core')
  const bridge = registerPlugin<MoonwakeBridge>('Moonwake')
  saved = bridge
  useDeviceStorage(new BridgeStorage(bridge))
}

export async function createNativePlatform(): Promise<Platform> {
  // Зовём всегда: площадку могут создать и без предварительного подключения,
  // а хранилище должно быть тем же самым, с приставкой малыша.
  await connectNativeStorage()
  const bridge = saved!

  // Разрешение спрашивает само приложение при первом запуске. Если человек отказал,
  // ничего не обещаем: строка на экране таймера скажет правду.
  const allowed = await bridge
    .notificationsAllowed()
    .then(({ allowed }) => allowed)
    .catch(() => false)
  deliveryMode.value = allowed ? 'push' : 'blocked'

  return {
    // То же хранилище, что и у сайта: с приставкой малыша
    storage: appStorage(),

    // Веб уже записал состояние в те же prefs; Kotlin перечитывает их и переставляет будильники,
    // обновляет виджет и отправляет снимок на часы.
    async stateChanged(_state, reminders = []) {
      await bridge.stateChanged()
      // Web Push в WebView нет: те же напоминания, что уходят серверу, здесь ставит система
      // Старая оболочка без этого метода не должна ронять смену окна
      if (typeof bridge.setReminders === 'function') await bridge.setReminders({ reminders }).catch(() => {})
    },

    vibrate(double) {
      void bridge.vibrate({ double })
    },

    // Экран держать не нужно: будильник разбудит систему сам.
    keepAwake() {},

    notifications: {
      get background() {
        return deliveryMode.value === 'push'
      },
      // Повторно спросить разрешение Android не даёт: человек меняет его в настройках системы.
      async enable() {
        const { allowed } = await bridge.notificationsAllowed()
        deliveryMode.value = allowed ? 'push' : 'blocked'
        return allowed
      },
    },
  }
}
